import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, Zap } from "lucide-react";

const curve = (rpm, peakRpm, peakVal, a, minFrac) => {
  const x = rpm / peakRpm;
  const v = Math.pow(x, a) * Math.exp(a * (1 - x));
  return Math.max(peakVal * minFrac, peakVal * Math.min(v, 1));
};

const buildData = (e) => {
  const diesel = e.fuel === "diesel";
  const redline = diesel ? 5000 : 6800;
  const tqPeak = diesel ? 2000 : 2400;
  const hpPeak = diesel ? 4000 : 5800;
  const pts = [];
  for (let r = 1000; r <= redline; r += 200) {
    pts.push({
      rpm: r,
      stockNm: Math.round(curve(r, tqPeak, e.stock_nm, 1.6, 0.35)),
      tunedNm: Math.round(curve(r, tqPeak, e.tuned_nm, 1.6, 0.35)),
      stockBhp: Math.round(curve(r, hpPeak, e.stock_bhp, 2.6, 0.2)),
      tunedBhp: Math.round(curve(r, hpPeak, e.tuned_bhp, 2.6, 0.2)),
    });
  }
  return pts;
};

const DynoTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = (k) => payload.find((p) => p.dataKey === k)?.value;
  return (
    <div className="glass-panel rounded-lg px-4 py-3 text-xs font-mono">
      <p className="text-neutral-400 mb-2 tracking-widest">{label} RPM</p>
      <p className="text-neutral-400">
        Stock: <span className="text-neutral-200">{get("stockBhp")} BHP</span>
        <span className="text-neutral-600"> / </span>
        <span className="text-neutral-200">{get("stockNm")} Nm</span>
      </p>
      <p className="text-nitro mt-1">
        Tuned: <span className="text-neutral-100">{get("tunedBhp")} BHP</span>
        <span className="text-neutral-600"> / </span>
        <span className="text-neutral-100">{get("tunedNm")} Nm</span>
      </p>
    </div>
  );
};

const LegendDot = ({ color, dashed, label }) => (
  <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400">
    <span
      className="inline-block w-6 h-0"
      style={{ borderTop: `3px ${dashed ? "dashed" : "solid"} ${color}` }}
    />
    {label}
  </span>
);

export const DynoGraph = ({ engine }) => {
  const data = useMemo(() => buildData(engine), [engine]);
  const dBhp = engine.tuned_bhp - engine.stock_bhp;
  const dNm = engine.tuned_nm - engine.stock_nm;

  return (
    <motion.div
      key={engine.name}
      data-testid="dyno-power-graph"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="mt-10 glass-panel rounded-2xl p-6 sm:p-10 relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-nitro/10 blur-[90px] pointer-events-none" />

      <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-nitro mb-2">
            Stage 1 Projection
          </p>
          <h3 className="font-heading font-bold uppercase text-2xl sm:text-3xl text-neutral-100 leading-tight">
            {engine.name}
          </h3>
          <div className="flex flex-wrap gap-5 mt-4">
            <LegendDot color="#64748B" label="Stock BHP" />
            <LegendDot color="#FF2E4D" label="Tuned BHP" />
            <LegendDot color="#64748B" dashed label="Stock Nm" />
            <LegendDot color="#FF2E4D" dashed label="Tuned Nm" />
          </div>
        </div>

        <div className="flex gap-4">
          <motion.div
            data-testid="dyno-bhp-gain-badge"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.4 }}
            className="border border-nitro/60 rounded-xl px-5 py-4 bg-nitro/10 shadow-[0_0_25px_rgba(255,46,77,0.25)]"
          >
            <p className="font-heading font-extrabold text-3xl text-nitro flex items-center gap-1">
              <TrendingUp size={22} /> +{dBhp}
            </p>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 mt-1">
              BHP · {engine.stock_bhp} → {engine.tuned_bhp}
            </p>
          </motion.div>
          <motion.div
            data-testid="dyno-nm-gain-badge"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.55 }}
            className="border border-[#262933] rounded-xl px-5 py-4 bg-panel"
          >
            <p className="font-heading font-extrabold text-3xl text-neutral-100 flex items-center gap-1">
              <Zap size={22} className="text-nitro" /> +{dNm}
            </p>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 mt-1">
              Nm · {engine.stock_nm} → {engine.tuned_nm}
            </p>
          </motion.div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={data} margin={{ top: 10, right: 4, left: 4, bottom: 0 }}>
          <CartesianGrid stroke="#1F232D" strokeDasharray="3 6" vertical={false} />
          <XAxis
            dataKey="rpm"
            stroke="#6B7280"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#6B7280" }}
            tickLine={false}
            axisLine={{ stroke: "#262933" }}
          />
          <YAxis
            yAxisId="bhp"
            stroke="#6B7280"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#6B7280" }}
            tickLine={false}
            axisLine={false}
            width={42}
          />
          <YAxis
            yAxisId="nm"
            orientation="right"
            stroke="#6B7280"
            tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#6B7280" }}
            tickLine={false}
            axisLine={false}
            width={42}
          />
          <Tooltip content={<DynoTooltip />} cursor={{ stroke: "#FF2E4D", strokeOpacity: 0.3 }} />
          <Area
            yAxisId="bhp"
            type="monotone"
            dataKey="tunedBhp"
            stroke="#FF2E4D"
            strokeWidth={2.5}
            fill="rgba(255,46,77,0.12)"
            dot={false}
            isAnimationActive
            animationDuration={1400}
          />
          <Line
            yAxisId="bhp"
            type="monotone"
            dataKey="stockBhp"
            stroke="#64748B"
            strokeWidth={2}
            dot={false}
            isAnimationActive
            animationDuration={1400}
          />
          <Line
            yAxisId="nm"
            type="monotone"
            dataKey="tunedNm"
            stroke="#FF2E4D"
            strokeDasharray="6 5"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive
            animationDuration={1800}
          />
          <Line
            yAxisId="nm"
            type="monotone"
            dataKey="stockNm"
            stroke="#64748B"
            strokeDasharray="6 5"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive
            animationDuration={1800}
          />
        </ComposedChart>
      </ResponsiveContainer>

      <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">
        Indicative Stage 1 figures — final results confirmed on the dyno
      </p>
    </motion.div>
  );
};
